import React, { useState } from 'react';
import FormDialog from '../../../Components/Modal/modal';
import {
  CANCEL_TEXT,
  EFFORT_UPLOAD,
  ADD_EDIT_RESPONSE_MESSAGES,
  AUTHENTICATION_FAILED,
} from '../../../Constants/strings';
import CommonDatePicker from '../../../Components/Datepicker/datepicker';
import './runEffortUpload.scss';
import PropTypes from 'prop-types';
import UseAxios from '../../../Api/http/http';
import { triggerEffortUpload } from '../../../Api/request/integration';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/en';
dayjs.extend(customParseFormat);

dayjs.locale({
  ...dayjs.Ls.en,
  formats: {
    ...dayjs.Ls.en.formats,
    LongDate: 'DD-MM-YYYY',
  },
});

const RunEffortUploadForm = ({
  openAddModal,
  handleClose,
  runBy,
  setRows,
}) => {
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [autoFocus, setAutoFocus] = useState(false);
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const { snackbarOpen } = useSnackbar();

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const resetValues = () => {
    setStartDateInput('');
    setEndDateInput('');
    setStartDateError('');
    setEndDateError('');
  };

  const handleApiResponse = (responseData) => {
    switch (
      responseData.data.status
      || responseData.data.response.data.statusCode
    ) {
    case 200:
      setRows();
      snackbarOpen(EFFORT_UPLOAD.EFFORT_UPLOAD_ADD_SUCCESS,
        'success',
      );
      handleCloseWithReset();
      break;
    case 400:
      snackbarOpen(
        responseData.data.response.data.externalMessage,
        'warning',
      );
      break;
    case 401:
      snackbarOpen(AUTHENTICATION_FAILED, 'warning');
      break;
    case 500:
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      break;
    default:
      snackbarOpen(EFFORT_UPLOAD.UNABLE_TO_ADD_EFFORT_UPLOAD,
        'warning',
      );
      handleCloseWithReset();
      break;
    }
  };

  const handleValidateFields = (reqData) => {
    let isValid = true;

    if (!reqData.startDate) {
      setStartDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE);
      isValid = false;
    } else {
      setStartDateError('');
    }

    if (!reqData.endDate) {
      setEndDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE);
      isValid = false;
    } else {
      setEndDateError('');
    }

    return isValid;
  };

  const submitAction = async () => {
    try {

      if (startDateError || endDateError) return;

      const requestData = {
        startDate: startDateInput,
        endDate: endDateInput,
        schedulerType: 'jira-work-log',
        runBy,
      };

      if (!handleValidateFields(requestData)) return;

      const response = await UseAxios(triggerEffortUpload(requestData));

      handleApiResponse(response);
      setAutoFocus(false);
    } catch (error) {
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_ADD_EFFORT_UPLOAD,
        'warning',
      );
      handleCloseWithReset();
      console.error('Error:', error);
    }
  };

  const handleSubmit = () => submitAction();

  const handleStartDate = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDate = dayjs(); // Ensure currentDate is a dayjs object
    // eslint-disable-next-line newline-per-chained-call
    const maxAllowedDate = currentDate.add(1, 'month').endOf('month').format('YYYY-MM-DD'); // End of next month
    const minDate = currentDate.subtract(2, 'month').format('YYYY-MM-DD');

    setStartDateInput(formattedDate);

    // Validate start date
    let startDateErrorMessage = '';
    const endDateErrorMessage = '';

    if (!formattedDate) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE;
    } else if (new Date(formattedDate) < new Date(minDate) || new Date(formattedDate) > new Date(maxAllowedDate)) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (endDateInput && new Date(formattedDate) > new Date(endDateInput)) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setStartDateError(startDateErrorMessage);
    setEndDateError(endDateErrorMessage);
  };

  const handleEndDate = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDate = dayjs(); // Ensure currentDate is a dayjs object
    const maxAllowedDate = currentDate.add(1, 'month').endOf('month')
      .format('YYYY-MM-DD'); // End of next month
    const minDate = currentDate.subtract(2, 'month').format('YYYY-MM-DD');

    setEndDateInput(formattedDate);

    // Validate end date
    let endDateErrorMessage = '';
    const startDateErrorMessage = '';

    if (!formattedDate) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE;
    } else if (new Date(formattedDate) < new Date(minDate) || new Date(formattedDate) > new Date(maxAllowedDate)) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (startDateInput && new Date(formattedDate) < new Date(startDateInput)) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setEndDateError(endDateErrorMessage);
    setStartDateError(startDateErrorMessage);
  };


  return (
    <div>
      <FormDialog
        title={EFFORT_UPLOAD.ADD_BUTTON_TEXT}
        open={openAddModal}
        button1Action={handleCloseWithReset}
        dialogPaperCss="effort-upload-dialog-paper"
        dialogCss="effort-upload-dialog"
        dialogBodycontent={
          <div className="effort-upload-dialog-body">
            <div className="effort-upload-start-date">
              <CommonDatePicker
                textCss="effort-upload-start-date-field"
                label={EFFORT_UPLOAD.SELECT_START_DATE}
                minDate={dayjs().subtract(2, 'month')}
                maxDate={dayjs().endOf('month')}
                placeholder={EFFORT_UPLOAD.PLACEHOLDER}
                autoFocus={autoFocus}
                value={startDateInput ? dayjs(startDateInput) : null}
                onChange={(date) =>
                  handleStartDate({
                    name: 'startDate',
                    value: date,
                  })
                }
                id={'add-start-date-text'}
                className="custom-calender"
              />
              {startDateError && (
                <div
                  className={'effort-upload-error'}
                >
                  <small>{startDateError}</small>
                </div>
              )}
            </div>
            <div className="effort-upload-start-date">
              <CommonDatePicker
                textCss="effort-upload-end-date-field"
                label={EFFORT_UPLOAD.SELECT_END_DATE}
                minDate={dayjs().subtract(2, 'month')}
                maxDate={dayjs().endOf('month')}
                placeholder={EFFORT_UPLOAD.PLACEHOLDER}
                autoFocus={autoFocus}
                value={endDateInput ? dayjs(endDateInput) : null}
                onChange={(date) =>
                  handleEndDate({
                    name: 'endDate',
                    value: date,
                  })
                }
                id={'add-end-date-text'}
                className="custom-calender"
              />
              {endDateError && (
                <div
                  className={'effort-upload-error'}
                >
                  <small>{endDateError}</small>
                </div>
              )}
            </div>

          </div>
        }
        button1={CANCEL_TEXT}
        button2={EFFORT_UPLOAD.RUN_BUTTON}
        button2Action={handleSubmit}
        cancelButtonId={'effort-upload-cancel-btn'}
        submitButtonId={'effort-upload-submit-btn'}
        isPopUp = {true}
      />
    </div>
  );
};

RunEffortUploadForm.propTypes = {
  openAddModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  runBy: PropTypes.string.isRequired,
  setRows: PropTypes.func.isRequired,
};

export default RunEffortUploadForm;
